import { xor, isUndefined, isObject } from 'lib';

export const toggleOneItemInList = ({ list = [], itemToToggle }) => {
  if (!Array.isArray(list) || !itemToToggle) {
    throw Error('list, listToToggle of toggleOneItemInList is required');
  }
  return xor(list, [itemToToggle]);
};

export const handleSetSelectedFilterIds = (state, action) => {
  const { id, idsFromUrl, componentName, parentPlatform } = action.payload;
  const filter = parentPlatform
    ? state.filters[parentPlatform][componentName]
    : state.filters[componentName];
  if (id) {
    filter.selectedIds = toggleOneItemInList({
      list: filter.selectedIds,
      itemToToggle: id,
    });
  }
  if (idsFromUrl) {
    filter.selectedIds = idsFromUrl;
    filter.isOpen = true;
  }
  filter.hasFilter = filter.selectedIds.length > 0;
};

export const handleSetSelectedItems = (state, action) => {
  const { componentName, item, isChecked, isFromUrl, parentPlatform } =
    action.payload;
  const filter = parentPlatform
    ? state.filters[parentPlatform][componentName]
    : state.filters[componentName];
  if (isChecked) {
    filter.selectedItems[item.id] = item;
  } else {
    delete filter.selectedItems[item.id];
  }
  filter.hasFilter = Object.keys(filter.selectedItems).length > 0;
  if (filter.selectedValue !== 'include') {
    filter.hasFilter = true;
  }
  if (isFromUrl) {
    filter.isOpen = true;
    filter.selectedItems = {};
    filter.selectedItems[item.id] = item;
  }
};

export const handleToggleCollapsed = (state, action) => {
  const { componentName, parentPlatform } = action.payload;
  const filter = parentPlatform
    ? state.filters[parentPlatform][componentName]
    : state.filters[componentName];
  filter.isOpen = !filter.isOpen;
};

export const handleSetSelectedFilterValue = (
  state,
  action,
  filtersInitialState
) => {
  const { value, filterType, componentName, isFromUrl, parentPlatform } =
    action.payload;
  const filter = parentPlatform
    ? state.filters[parentPlatform][componentName]
    : state.filters[componentName];
  const filterInitialState = parentPlatform
    ? filtersInitialState[parentPlatform][componentName]
    : filtersInitialState[componentName];
  if (filterType) {
    filter.filterType = filterType;
  } else {
    filter.selectedValue = value;
  }
  filter.hasFilter = filter.selectedValue !== filterInitialState.selectedValue;
  if (Object.keys(filter.selectedItems || {}).length > 0) {
    filter.hasFilter = true;
  }
  if (isFromUrl) {
    filter.isOpen = true;
  }
};
export const handleSetPublishRateValue = (state, action) => {
  const { value, componentName, parentPlatform } = action.payload;
  const filter = parentPlatform
    ? state.filters[parentPlatform][componentName]
    : state.filters[componentName];
  filter.value = value;
  filter.hasFilter = value;
};

export const handleResetFilter = (state, action, filtersInitialState) => {
  const { componentName, parentPlatform } = action.payload;
  if (parentPlatform) {
    state.filters[parentPlatform][componentName] = {
      ...filtersInitialState[parentPlatform][componentName],
      isOpen: state.filters[parentPlatform][componentName].isOpen,
    };
  } else {
    state.filters[componentName] = {
      ...filtersInitialState[componentName],
      isOpen: state.filters[componentName].isOpen,
    };
  }
};

export const handleIncludeExcludeFilter = (state, action) => {
  const { componentName, parentPlatform, value } = action.payload;
  const filter = parentPlatform
    ? state.filters[parentPlatform][componentName]
    : state.filters[componentName];
  filter.filterType = value;
};

export const handleSetSelectedSentimentValues = (state, action) => {
  const { id, value, componentName, parentPlatform } = action.payload;

  const filter = parentPlatform
    ? state.filters[parentPlatform][componentName]
    : state.filters[componentName];

  filter.selectedValues[id] = value;
  filter.hasFilter = Object.values(filter.selectedValues).some((val) => val);
};

export const handleSetAdvertFilterValues = (state, action) => {
  const { active, min, max, componentName, parentPlatform } = action.payload;
  const filter = parentPlatform
    ? state.filters[parentPlatform][componentName]
    : state.filters[componentName];
  filter.selectedObjectValues = {
    active,
    min,
    max,
  };
  filter.hasFilter = true;

  if (!filter.selectedObjectValues?.active) {
    filter.hasFilter = false;
  }
};

export const handleSetSelectedContentBasedValues = (state, action) => {
  const { keyName, active, min, max, componentName, parentPlatform, id } =
    action.payload;
  const filter = parentPlatform
    ? state.filters[parentPlatform][componentName]
    : state.filters[componentName];
  if (!isUndefined(active)) {
    filter.selectedObjects[keyName].active = active;
  }
  if (!isUndefined(id)) {
    filter.selectedObjects[keyName].selectedIds = toggleOneItemInList({
      list: filter.selectedObjects[keyName].selectedIds,
      itemToToggle: id,
    });
  } else {
    filter.selectedObjects[keyName].min = min || null;
    filter.selectedObjects[keyName].max = max || null;
  }
  filter.hasFilter = Object.values(filter.selectedObjects).some(
    (subFilter) => subFilter?.active
  );
};

export const handleSetSelectedCategories = (state, action) => {
  const {
    categoryKey,
    isChecked,
    subCategories,
    componentName,
    parentPlatform,
  } = action.payload;
  const filter = parentPlatform
    ? state.filters[parentPlatform][componentName]
    : state.filters[componentName];
  const isCategoryChanged = subCategories.length > 1;
  if (isCategoryChanged) {
    filter.selectedCategories[categoryKey] = subCategories;
    if (!isChecked) {
      delete filter.selectedCategories[categoryKey];
    }
  } else {
    filter.selectedCategories[categoryKey] = toggleOneItemInList({
      list: filter.selectedCategories[categoryKey],
      itemToToggle: subCategories[0],
    });
    if (filter.selectedCategories[categoryKey]?.length === 0) {
      delete filter.selectedCategories[categoryKey];
    }
  }
  filter.hasFilter = Object.keys(filter.selectedCategories).length > 0;
};

export const handleChangeSortBy = (state, action) => {
  const { selected, order, parentPlatform } = action.payload;
  const sortBy = parentPlatform ? state.sortBy[parentPlatform] : state.sortBy;
  if (selected) {
    sortBy.selected = selected;
  }
  if (order) {
    sortBy.order = order;
  }
};

export const isDisabledFilter = (filterName, filters) => {
  const contentBasedFilters = [
    'influenceRate',
    'like',
    'reaction',
    'comment',
    'copy',
    'view',
  ];

  if (contentBasedFilters.includes(filterName)) {
    return isObject(filters?.contentBasedFilter?.selectedObjects?.[filterName])
      ? !filters?.contentBasedFilter?.selectedObjects?.[filterName]?.active
      : undefined;
  }
  return isObject(filters?.[filterName])
    ? filters?.[filterName]?.isDisabled
    : true;
};
